import { ReactNode, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styles from "./style.module.scss";
import PrintPage from "@components/print/PrintElements/PrintPage";
import PrintPageHeader from "@components/print/PrintElements/PrintPageHeader";
import PrintPageFooter from "@components/print/PrintElements/PrintPageFooter";
import { useAppSelector } from "@/store/hooks";
import PrintDocumentHeader from "@components/print/PrintElements/PrintDocumentHeader";
import DocumentInfo from "@components/print/PrintElements/DocumentInfo";
import ProductList from "@components/print/PrintElements/ProductList";
import { QuoteApi, QuoteDataType } from "@/api/QuoteApi";
import { QuoteFormDataKeys } from "@/generated/QuoteFormDataKeys";
import { QuoteRelatedAttributes } from "@/formTypes/QuoteDataType";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { readDocumentOtherChargesTotal } from "@utils/DocumentOtherCharges";
import { readDocumentOtherChargesLabel } from "@utils/ClientShipping";
import { formatDate } from "@utils/Api";
import { CustomerInfoBase } from "@/formTypes/DocumentClientType";
import { chunkArray } from "@utils/Utils";
import { sortProductsByOrder } from "@utils/EDocument";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";

const QuotePrint = () => {
  const params = useParams()
  const { id: quoteId, autoPrint = false, copies = 0 } = params;

  const suscription = useAppSelector((state) => { return state.suscription.suscription });
  const maxItemPerPage = Number(suscription?.items_per_page || 30)
  const [quote, setQuote] = useState<QuoteDataType>({})
  const [quoteProdCount, setQuoteProdCount] = useState<number>(0)
  const [quotesProducts, setQuotesProducts] = useState<EDocumentProductDataType[]>()
  const [documentPages, setDocumentPages] = useState<EDocumentProductDataType[][]>()

  useEffect(() => {
    import("@printerVariation/generic.scss");
    PrintPageDataLoadedPubSub.publish({ dataLoaded: false })
    loadQuoteData()
  }, []);

  useEffect(() => {
    const pages = chunkArray(quotesProducts || [], maxItemPerPage)
    setDocumentPages(pages)
  }, [maxItemPerPage, quotesProducts]);

  const loadQuoteData = async () => {
    if (quoteId) {
      const quote = await QuoteApi.get(quoteId, {
        selectionSet: [
          ...QuoteFormDataKeys.filter(k => !QuoteRelatedAttributes.includes(k)),
          "currency.*",
          "client.*",
          "quoteProducts.*",
          "quoteProducts.product.*"
        ]
      })

      setQuotesProducts(sortProductsByOrder(quote.quoteProducts))
      setQuoteProdCount(quote.quoteProducts?.length || 0)
      setQuote(quote)
    }
    PrintPageDataLoadedPubSub.publish({ dataLoaded: true })
  }

  function toCustomerInfoBase(p: QuoteDataType): CustomerInfoBase {
    if (!p?.nombre_factura) throw new Error("nombre_factura requerido");
    return {
      nombre_factura: p.nombre_factura,
      razon_social: p.razon_social ?? "",
      tel1: p.tel1 ?? "",
      tel2: p.tel2 ?? "",
      email: p.email ?? "",
      direccion: p.direccion ?? "",
      clientActivitycode: p.clientActivitycode ?? "",
      plazo: p.plazo ?? null,
      client: p.client ?? null,
      tipo_identificacion: p.tipo_identificacion ?? "",
      identificacion: p.identificacion ?? "",
    };
  }

  const renderPage = (documentPages: EDocumentProductDataType[][]): ReactNode[] => {
    return documentPages.map((page, index) => {
      return (
        <PrintPage
          autoPrint={autoPrint === "1"}
          copies={Number(copies)}
        >
          <PrintPageHeader suscription={suscription} />
          <div className="page-content">
            <PrintDocumentHeader title={`Cotizacion #${quote.numero}`} fecha={formatDate(quote.fecha, "YYYY-MM-DD", "DD.MM.YY")} />
            <DocumentInfo
              customer={toCustomerInfoBase(quote)}
              docType="Factura"
            />
            <ProductList
              maxItemPerPage={maxItemPerPage}
              products={page}
              productCount={quoteProdCount}
            />
            <PrintPageFooter
              currencyCode={quote.currency.currency_id}
              exchangeRate={quote.exchange_rate || 0}
              comments={[
                `${suscription?.quote_info}`,
                `${quote.comments}`
              ]}
              summary={{
                subtotal: quote.subtotal || 0,
                descuento: quote.descuento || 0,
                impuestos: quote.impuestos || 0,
                total: quote.total || 0,
                otrosCargos: readDocumentOtherChargesTotal(quote),
                otrosCargosLabel: readDocumentOtherChargesLabel(quote)
              }}
              currentPage={(index + 1)}
              totalPages={documentPages.length} />
          </div>

        </PrintPage>
      )
    });
  }

  return (
    <div>
      {quote?.id && documentPages && renderPage(documentPages)}
      {!quote.id && (
        <h3>Cargando</h3>
      )}

    </div>
  );
}

export default QuotePrint;
